import { EmptyState, SectionCard } from '@atlas/ui-components'
import { Skeleton } from '@atlas/ui-primitives'
import { useCallback, useState } from 'react'
import { AssetGridSkeleton } from '../asset-tile/asset-grid-skeleton'
import { ASSET_LIST_TILE_GRID } from '../asset-tile/asset-thumb-tile'
import type { AssetListBulkAction, AssetListRole } from './asset-list-card.types'
import { AssetListGroup } from './asset-list-group'
import { AssetListSelectionBar } from './asset-list-selection-bar'
import { AssetListSelectionStatus } from './asset-list-selection-status'
import { type DownloadAssets, useAssetListDownload } from './use-asset-list-download'
import { useAssetListSelection } from './use-asset-list-selection'

/**
 * The card's body while it is being read: two stand-in role groups, because the list is always
 * read as roles rather than as a flat list - one grid would collapse into a plain asset grid
 * and read as the wrong section.
 *
 * Exported so a host's own first-read skeleton stands the section in the same way this card
 * does, rather than inventing a second shape for the same wait.
 */
export function AssetListBodySkeleton({ label }: { label?: string }) {
	return (
		<div className="space-y-ds-6" aria-busy="true">
			{label ? <span className="sr-only">{label}</span> : null}
			{Array.from({ length: 2 }, (_, i) => (
				<section key={i}>
					<Skeleton shape="text" className="mb-ds-3 h-3 w-24" />
					{/* The list's own grid, not the delivered-asset one: a skeleton on different
					    columns would reflow the section the moment the real tiles land. */}
					<AssetGridSkeleton className={ASSET_LIST_TILE_GRID} count={4} />
				</section>
			))}
		</div>
	)
}

/** How the card describes the outcome of a bulk download, or `null` when it has nothing to report. */
function downloadOutcome(startedCount: number, failedFileNames: string[], hasFailed: boolean): string | null {
	if (hasFailed) {
		return `Could not start the download of ${failedFileNames.join(', ')}. Please retry.`
	}
	if (startedCount === 0) return null
	// "Started", never "completed": once the browser owns a transfer, its outcome is not
	// observable from here.
	const started = `${startedCount} ${startedCount === 1 ? 'download' : 'downloads'} started.`
	return failedFileNames.length === 0 ? started : `${started} Could not start ${failedFileNames.join(', ')}.`
}

export interface AssetListCardProps {
	/** The card's heading, unscoped - the host names the list this card is standing for. */
	title: string
	/** The list's roles, already grouped and ordered by the host. */
	roles: AssetListRole[]
	onOpenAsset: (assetId: string) => void
	/**
	 * How the host downloads a whole selection. Selection is offered ONLY when this is given:
	 * without a download there is nothing to select for, and an affordance that leads nowhere is
	 * worse than none.
	 */
	onDownloadAssets?: DownloadAssets
	/**
	 * The host's own words for that action, where it is not a download - composing a package,
	 * say. Absent, the card names it a download exactly as it always has.
	 */
	bulkAction?: AssetListBulkAction
	/** The scope the list is narrowed to, appended to the heading so a short list reads as narrowed. */
	scopeLabel?: string | null
	/** What an empty list means here - the host knows whether its own filters narrowed it away. */
	emptyLabel: string
	isLoading?: boolean
	/** What the wait is called for anyone who cannot see the pulse. */
	loadingLabel?: string
	isError?: boolean
	/** What a failed read says, when the host can name the list better than this card can. */
	errorLabel?: string
	/**
	 * How the host imports files into one group. An import surface is offered on every group
	 * ONLY when this is given, and the card hands back the files plus the CODE of the group
	 * they landed on.
	 *
	 * The card neither uploads nor classifies, and does not interpret the code it passes back -
	 * which is what lets the same card serve a host that classifies from a work order's tuple
	 * and one that does something else entirely. It also reports no outcome for an import: the
	 * file arrives back through the host's own read, so a second report here would contradict
	 * the refreshed list the moment it lands.
	 */
	onImportFiles?: (roleCode: string, files: File[]) => void
}

/**
 * A titled list of reusable assets - grouped by the role each asset plays (logo, icons,
 * font, …) rather than listed flat, so a missing role reads as an absent group instead of
 * hiding in a filename list. Each role is a collapsible group, open on arrival, whose label
 * and count stay readable while collapsed.
 *
 * The words are the host's - the heading, and what an empty, loading or failed list says - so
 * the same card can stand for any set of assets rather than for one section's subject. Only
 * the wording that is true of any asset list, such as what selecting changed, is the card's
 * own.
 *
 * The header counts distinct assets, so it reads lower than the group totals when an asset
 * carries more than one role. Selection is keyed by asset id for the same reason: an asset
 * serving two roles is one asset to select and one asset to download.
 *
 * Selecting is a MODE the card holds: outside it the tiles are links to their assets, inside
 * it they are toggles. That keeps one action per tile, so a mis-click can neither lose a
 * selection nor navigate away from one.
 *
 * A role holding no assets renders as a stated, zero-count group rather than being dropped -
 * the host supplies its roles, so an empty one is a claim the list makes. Where the host
 * offers {@link AssetListCardProps.onImportFiles}, that group is also a place to import into,
 * unless the role itself declines.
 */
export function AssetListCard({
	title,
	roles,
	onOpenAsset,
	onDownloadAssets,
	bulkAction,
	scopeLabel,
	emptyLabel,
	isLoading,
	loadingLabel = 'Loading the assets…',
	isError,
	errorLabel = 'Could not load these assets. Please retry.',
	onImportFiles,
}: AssetListCardProps) {
	// Distinct assets: an asset listed under two roles is one asset the list holds.
	const assets = [...new Map(roles.flatMap((role) => role.assets).map((asset) => [asset.id, asset])).values()]
	// Collapsed rather than expanded codes, so the empty state is "every group open" and a
	// role that appears later opens with the rest instead of arriving hidden.
	const [collapsedCodes, setCollapsedCodes] = useState<ReadonlySet<string>>(new Set())
	// An asset still uploading is not stored yet, so it can be neither selected nor downloaded -
	// and it must not be what makes the card offer selecting at all.
	const storedAssets = assets.filter((asset) => asset.uploadProgress === undefined)
	const selection = useAssetListSelection(storedAssets)
	const download = useAssetListDownload(onDownloadAssets)
	const selectable = onDownloadAssets !== undefined

	const toggleExpanded = useCallback((code: string) => {
		setCollapsedCodes((current) => {
			const next = new Set(current)
			if (!next.delete(code)) next.add(code)
			return next
		})
	}, [])

	// Leaving the mode takes the last run's report with it: it describes a selection that no
	// longer exists.
	const exitSelecting = useCallback(() => {
		selection.exitSelecting()
		download.reset()
	}, [selection, download])

	// The same is true when the asset set changes underneath the card rather than the reader
	// leaving: the selection is dropped during render, so the report scoped to it goes too.
	// During render, not in an effect, so no frame paints a report about assets already gone.
	if (selection.didAssetSetChange && (download.startedCount > 0 || download.failedFileNames.length > 0)) {
		download.reset()
	}

	// A run reports itself by what it did: started downloads, or files it could not start. Before
	// the first run, and after a reset, there is neither - and so nothing to report.
	const hasRunOutcome = download.startedCount > 0 || download.failedFileNames.length > 0

	// Counted on the ASSETS, not on the roles: a list of nothing but empty groups has nothing
	// to select for, and the selection bar would lead nowhere.
	const hasAssets = !isLoading && !isError && storedAssets.length > 0

	return (
		<SectionCard
			title={scopeLabel ? `${title} · ${scopeLabel}` : title}
			count={isLoading || isError ? undefined : assets.length}
			action={
				selectable && hasAssets ? (
					<AssetListSelectionBar
						isSelecting={selection.isSelecting}
						selectionCount={selection.selectedAssets.length}
						isDownloadPending={download.isPending}
						onStartSelecting={selection.startSelecting}
						onExitSelecting={exitSelecting}
						onDownloadSelected={() => void download.download(selection.selectedAssets)}
						action={bulkAction}
					/>
				) : null
			}
		>
			{isLoading ? (
				<AssetListBodySkeleton label={loadingLabel} />
			) : isError ? (
				<p className="text-ds-sm leading-5 text-ds-text-muted">{errorLabel}</p>
			) : roles.length === 0 ? (
				<EmptyState title={emptyLabel} />
			) : (
				<div className="space-y-ds-6">
					<AssetListSelectionStatus
						isSelecting={selection.isSelecting}
						// The host's whole sentence where it named the action, never a half-translated one:
						// "Generate package" above "3 downloads started" is two vocabularies for one
						// gesture. Whether the run FAILED stays the card's to say - that is a fact about
						// the run, not a wording choice.
						//
						// Asked only once a run has reported something. A host's wording is written for a
						// finished run, so consulting it before one would have the card state that outcome
						// on arrival - an untouched selection reading as a failed one.
						outcome={
							hasRunOutcome
								? bulkAction?.outcome
									? bulkAction.outcome(download.startedCount, download.failedFileNames)
									: downloadOutcome(download.startedCount, download.failedFileNames, download.hasFailed)
								: null
						}
						isOutcomeFailure={download.hasFailed}
					/>
					{roles.map((role) => (
						<AssetListGroup
							key={role.code}
							role={role}
							expanded={!collapsedCodes.has(role.code)}
							onToggleExpanded={toggleExpanded}
							selectionMode={selection.isSelecting}
							selectable={selectable}
							selectedAssetIds={selection.selectedAssetIds}
							onToggleSelection={selection.toggleSelection}
							onToggleGroupSelection={selection.toggleGroupSelection}
							onOpenAsset={onOpenAsset}
							// Withheld while selecting: in that mode every control on a group means
							// "include this in the selection", and an import would be the one that
							// means something else. The loading and failed branches render no groups
							// at all, so they need no gate of their own. A role that declines imports
							// withholds it too - the card offers them, the role accepts them.
							onImportFiles={selection.isSelecting || role.importable === false ? undefined : onImportFiles}
						/>
					))}
				</div>
			)}
		</SectionCard>
	)
}
